import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { assistantService, ChatMessage, AssistantAction } from '../services/AssistantService';
import { ttsService } from '../services/TtsService';
import { getVoice } from '../models/assistantVoices';

interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  actions?: AssistantAction[];
  error?: boolean;
}

const GREETING: DisplayMessage = {
  role: 'assistant',
  content:
    "Hi, I'm VERA. I can help you get around the app, explain features, or help word your notes. Try “open review notes” or ask me anything.",
};

export const AssistantPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [speakOn, setSpeakOn] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  // Sync the speak toggle with the saved per-user preference.
  useEffect(() => {
    setSpeakOn(!!user?.assistantVoiceEnabled);
  }, [user?.assistantVoiceEnabled]);

  // Stop any speech when the panel closes.
  useEffect(() => {
    if (!open) ttsService.stop();
  }, [open]);

  // Only show the assistant to signed-in users.
  if (!user) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const nextMessages: DisplayMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setBusy(true);
    try {
      const history: ChatMessage[] = nextMessages
        .filter((m) => !m.error)
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));
      const reply = await assistantService.ask(text, history, user.name, user.role);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply.response, actions: reply.actions },
      ]);
      if (speakOn) {
        ttsService.speak(reply.response, getVoice(user.assistantVoice).voiceId);
      }
    } catch (e) {
      console.error('assistant error:', e);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I couldn't reach the assistant just now. Please try again.",
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const runAction = (action: AssistantAction) => {
    if (action.type === 'navigate' && action.data?.path) {
      navigate(action.data.path);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          aria-label="Open VERA assistant"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 150,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '999px',
            boxShadow: 'var(--shadow-lg)',
            cursor: 'pointer',
            fontWeight: 'var(--font-semibold)',
          }}
        >
          <Sparkles size={18} />
          Ask VERA
        </button>
      )}

      {/* Chat drawer */}
      {open && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 150,
            width: 'min(380px, calc(100vw - 40px))',
            height: 'min(560px, calc(100vh - 40px))',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-3) var(--spacing-4)',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-white)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', fontWeight: 'var(--font-semibold)' }}>
              <Sparkles size={18} /> VERA Assistant
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <button
                onClick={() => {
                  setSpeakOn((s) => {
                    if (s) ttsService.stop();
                    return !s;
                  });
                }}
                aria-label={speakOn ? 'Mute voice' : 'Enable voice'}
                title={speakOn ? 'Voice on — click to mute' : 'Voice off — click to enable'}
                style={{ background: 'none', border: 'none', color: 'var(--color-white)', cursor: 'pointer', display: 'inline-flex', opacity: speakOn ? 1 : 0.6 }}
              >
                {speakOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
                style={{ background: 'none', border: 'none', color: 'var(--color-white)', cursor: 'pointer', display: 'inline-flex' }}
              >
                <X size={18} />
              </button>
            </span>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 'var(--spacing-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-3)',
              backgroundColor: 'var(--content-bg)',
            }}
          >
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div
                  style={{
                    maxWidth: '85%',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    backgroundColor: m.role === 'user' ? 'var(--color-primary)' : 'var(--color-white)',
                    color: m.role === 'user' ? 'var(--color-white)' : (m.error ? 'var(--color-error)' : 'var(--color-text-primary)'),
                    border: m.role === 'user' ? 'none' : '1px solid var(--color-border)',
                  }}
                >
                  {m.content}
                </div>
                {m.actions?.map(
                  (a, j) =>
                    a.type === 'navigate' &&
                    a.data?.label && (
                      <button
                        key={j}
                        onClick={() => runAction(a)}
                        style={{
                          marginTop: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          color: 'var(--color-primary)',
                          border: '1px solid var(--color-primary)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          cursor: 'pointer',
                        }}
                      >
                        Open {a.data.label} <ArrowRight size={14} />
                      </button>
                    )
                )}
              </div>
            ))}
            {busy && (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                VERA is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', padding: 'var(--spacing-3)', borderTop: '1px solid var(--color-border)' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask VERA…"
              disabled={busy}
              style={{
                flex: 1,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-family-base)',
                color: 'var(--color-text-primary)',
                backgroundColor: 'var(--color-white)',
              }}
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              aria-label="Send"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || !input.trim() ? 0.6 : 1,
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantPanel;
